import React, { useState } from "react";
import {
  QrCode,
  ExternalLink,
  Copy,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  Shield,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  getProductDppQr,
  retryDppRegistry,
  publishProductDpp,
  mapDppFromStatusResponse,
} from "../../../../../apis/Product/ProductEPDApi";

const registryMeta = {
  not_registered: {
    label: "Not registered",
    color: "text-gray-400 bg-gray-400/15 border-gray-400/30",
    icon: Clock,
  },
  pending: {
    label: "Pending",
    color: "text-amber-300 bg-amber-400/15 border-amber-400/30",
    icon: Clock,
  },
  registered: {
    label: "Registered",
    color: "text-emerald-300 bg-emerald-400/15 border-emerald-400/30",
    icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    color: "text-red-300 bg-red-400/15 border-red-400/30",
    icon: AlertCircle,
  },
};

function RegistryBadge({ status }) {
  const meta = registryMeta[status] || registryMeta.not_registered;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${meta.color}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {meta.label}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

const DPPPanel = ({ productId, businessId, dppData, onDppUpdate, lcaLockStatus }) => {
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  const isEpdLocked = lcaLockStatus === "epd_locked";

  if (!isEpdLocked || (!dppData?.dpp_is_published && !dppData?.dpp_uid)) {
    return null;
  }

  const fetchQr = () => {
    getProductDppQr({
      productId,
      businessId,
      onSuccess: (res) => {
        // API shape: { message, data: { qr_code_png_base64, ... } }
        const data = res?.data?.qr_code_png_base64
          ? res.data
          : res?.data ?? res;
        setQrData(data);
        setQrLoading(false);
      },
      onError: (err) => {
        const msg =
          err?.response?.data?.message || "Could not load QR code.";
        const needsPublish =
          /not published|epd lock/i.test(msg) && isEpdLocked;

        if (needsPublish) {
          // Recovery: product is epd_locked but DPP publish didn't finish
          publishProductDpp({
            productId,
            businessId,
            onSuccess: (pubRes) => {
              const updated = mapDppFromStatusResponse(pubRes);
              if (updated) onDppUpdate?.(updated);
              getProductDppQr({
                productId,
                businessId,
                onSuccess: (res) => {
                  const data = res?.data ?? res;
                  setQrData(data);
                  setQrLoading(false);
                },
                onError: (qrErr) => {
                  setQrLoading(false);
                  setQrModalOpen(false);
                  Swal.fire({
                    icon: "error",
                    title: "Error",
                    text:
                      qrErr?.response?.data?.message ||
                      "Could not load QR code after publish recovery.",
                  });
                },
              });
            },
            onError: () => {
              setQrLoading(false);
              setQrModalOpen(false);
              Swal.fire({
                icon: "error",
                title: "DPP not ready",
                text: "Product must be EPD-locked with a published DPP before downloading the QR. Click Lock EPD & Publish DPP first.",
              });
            },
          });
          return;
        }

        setQrLoading(false);
        setQrModalOpen(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: msg,
        });
      },
    });
  };

  const handleDownloadQr = () => {
    if (!productId || !businessId) return;
    if (!isEpdLocked) {
      Swal.fire({
        icon: "info",
        title: "EPD not locked",
        text: "Click Lock EPD & Publish DPP first, then download the QR.",
      });
      return;
    }
    setQrLoading(true);
    setQrModalOpen(true);
    fetchQr();
  };

  const handleCopyUrl = async () => {
    if (!dppData?.dpp_url) return;
    try {
      await navigator.clipboard.writeText(dppData.dpp_url);
      Swal.fire({
        icon: "success",
        title: "Copied",
        text: "Passport URL copied to clipboard.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Copy failed",
        text: "Could not copy URL to clipboard.",
      });
    }
  };

  const handleViewPassport = () => {
    if (dppData?.dpp_url) {
      window.open(dppData.dpp_url, "_blank", "noopener,noreferrer");
    }
  };

  const getQrImageSrc = (data = qrData) => {
    if (!data) return null;
    const raw =
      data.qr_code_png_base64 ??
      data.qr_code_base64 ??
      data.qr_png_base64 ??
      data.png_base64 ??
      null;
    if (!raw || typeof raw !== "string") return null;
    if (raw.startsWith("data:")) return raw;
    return `data:image/png;base64,${raw.replace(/\s/g, "")}`;
  };

  // Must stay fully synchronous — Chrome blocks download if we await first.
  const handleDownloadPng = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const filename = `dpp-qr-${productId || "passport"}.png`;
    const img = document.getElementById("dpp-qr-download-img");
    const src = img?.currentSrc || img?.src || getQrImageSrc();

    if (!src) {
      Swal.fire({
        icon: "error",
        title: "No QR image",
        text: "QR data is missing. Close the modal and try Download QR again.",
      });
      return;
    }

    try {
      let dataUrl = src;

      // Prefer painting the visible <img> (handles odd base64 payloads)
      if (img && img.complete && img.naturalWidth > 0) {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        dataUrl = canvas.toDataURL("image/png");
      }

      const base64 = (dataUrl.split(",")[1] || "").replace(/\s/g, "");
      if (!base64) throw new Error("empty base64");

      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "image/png" });
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
    } catch (err) {
      console.error("QR PNG download failed:", err);
      // Native <a download> fallback (no await)
      const fallback = document.createElement("a");
      fallback.href = src;
      fallback.download = filename;
      fallback.style.display = "none";
      document.body.appendChild(fallback);
      fallback.click();
      document.body.removeChild(fallback);
    }
  };

  const handleRetryRegistry = () => {
    if (!productId || !businessId) return;
    setRetryLoading(true);
    retryDppRegistry({
      productId,
      businessId,
      onSuccess: (res) => {
        const updated = mapDppFromStatusResponse(res);
        if (updated) onDppUpdate?.(updated);
        setRetryLoading(false);
        Swal.fire({
          icon: "success",
          title: "Registry retry submitted",
          text: "Registry registration has been re-queued.",
          timer: 2500,
          showConfirmButton: false,
        });
      },
      onError: (err) => {
        setRetryLoading(false);
        Swal.fire({
          icon: "error",
          title: "Retry failed",
          text: err?.response?.data?.message || "Could not retry registry registration.",
        });
      },
    });
  };

  return (
    <>
      <div
        className="rounded-3xl overflow-hidden shadow-lg mt-6"
        style={{ background: "hsl(221 39% 24%)" }}
      >
        <div className="p-8 space-y-4">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-white/70 flex-shrink-0" />
            <h3 className="text-sm font-semibold text-white">Digital Product Passport</h3>
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border text-emerald-300 bg-emerald-400/15 border-emerald-400/30">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Published
            </span>
          </div>

          <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">UID</span>
              <span className="text-white font-mono font-semibold text-[11px]">
                {dppData.dpp_uid || "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">Registry</span>
              <RegistryBadge status={dppData.dpp_registry_status} />
            </div>
            {dppData.dpp_registry_id && (
              <div className="flex items-center justify-between">
                <span className="text-white/50 uppercase tracking-wide text-[10px]">
                  Registry ID
                </span>
                <span className="text-white/80 font-mono text-[11px]">
                  {dppData.dpp_registry_id}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">OpenCirc</span>
              <span className="text-white/80">
                {dppData.opencirc_passport_id ? (
                  <span className="text-emerald-400 font-medium">Synced ✓</span>
                ) : (
                  "—"
                )}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/50 uppercase tracking-wide text-[10px]">Published</span>
              <span className="text-white/80">{formatDate(dppData.dpp_published_at)}</span>
            </div>
            {dppData.dpp_url && (
              <div className="pt-1 border-t border-white/10">
                <span className="text-white/50 uppercase tracking-wide text-[10px] block mb-1">
                  Passport URL
                </span>
                <span className="text-white/80 text-[11px] break-all">{dppData.dpp_url}</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={handleCopyUrl}
              disabled={!dppData.dpp_url}
              className="w-full h-10 text-xs font-semibold border border-white/20 text-white bg-transparent hover:bg-white/10 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-40"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy URL
            </button>
            <button
              type="button"
              onClick={handleViewPassport}
              disabled={!dppData.dpp_url}
              className="w-full h-10 text-xs font-semibold border border-white/20 text-white bg-transparent hover:bg-white/10 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-40"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View Passport
            </button>
            <button
              type="button"
              onClick={handleDownloadQr}
              className="w-full h-10 text-xs font-semibold bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl flex items-center justify-center gap-1.5"
            >
              <QrCode className="h-3.5 w-3.5" />
              Download QR
            </button>
            {dppData.dpp_registry_status === "failed" && (
              <button
                type="button"
                onClick={handleRetryRegistry}
                disabled={retryLoading}
                className="w-full h-10 text-xs font-semibold border border-red-400/40 text-red-300 bg-transparent hover:bg-red-500/10 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-40"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${retryLoading ? "animate-spin" : ""}`} />
                {retryLoading ? "Retrying…" : "Retry Registry Registration"}
              </button>
            )}
          </div>
        </div>
      </div>

      {qrModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setQrModalOpen(false)}
            aria-hidden="true"
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-sm w-full mx-4 p-6">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="h-5 w-5 text-emerald-600" />
              <h2 className="text-lg font-semibold">DPP QR Code</h2>
            </div>

            {qrLoading ? (
              <p className="text-sm text-gray-500 text-center py-8">Loading QR code…</p>
            ) : getQrImageSrc() ? (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <img
                    id="dpp-qr-download-img"
                    src={getQrImageSrc()}
                    alt="DPP QR Code"
                    className="w-48 h-48 border border-gray-200 dark:border-gray-700 rounded-lg"
                  />
                </div>
                {(qrData?.dpp_url || dppData?.dpp_url) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center break-all">
                    {qrData?.dpp_url || dppData?.dpp_url}
                  </p>
                )}
                {qrData?.gs1_digital_link && (
                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 text-center">
                    GS1 Digital Link (GTIN)
                  </p>
                )}
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setQrModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    Close
                  </button>
                  <a
                    href={getQrImageSrc()}
                    download={`dpp-qr-${productId || "passport"}.png`}
                    onClick={handleDownloadPng}
                    className="px-4 py-2 text-sm font-semibold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg inline-flex items-center no-underline cursor-pointer"
                  >
                    Download PNG
                  </a>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">QR code not available.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DPPPanel;
